// The island world, generated from the run seed on every machine. Geometry never
// crosses the wire: same seed, same island, same collision.
//
// Every island is one of a handful of ARCHETYPES (sim/landforms.ts), each laid out around the
// jetpack: canyons to thread, spires to perch on and orbit, arches and floating shelves to fly
// under, and water. Water is the heat economy's highway: wading, skimming low over it or flying
// through a waterfall dumps heat (sim/player.ts), so a stream that runs to the island edge and
// pours off it is a line of free flight across the map. See web/ISLANDS.md.

import { Rng, clamp, fbm, hash32, smoothstep, valueNoise, len2 } from './core';
import { layoutForms, layoutLand, layoutWater } from './landforms';
import { addSaloonCollision, saloonCeiling } from './saloon';
import { Archway, Destruct, layoutArchways } from './destruct';

export const NO_GROUND = -1e9;

export interface Island {
  cx: number;
  cz: number;
  baseY: number;
  radius: number;
  seed: number;
  main: boolean;
}

export type ObstacleKind = 'building' | 'ruin' | 'crate' | 'tank' | 'rock' | 'pillar' | 'pole' | 'gate'
  /** landforms (sim/landforms.ts): a tall rock stack with a standable crown */
  | 'spire'
  /** the span of a stone arch (a box held up by its legs or the canyon walls): walk on it, fly under it */
  | 'arch'
  /** an arch's leg */
  | 'archleg'
  /** a floating rock shelf: a slab hanging in the air, its underside a little island's strata */
  | 'shelf'
  /** the hanging underside of a shelf (same variant as its shelf) */
  | 'keel'
  /** the saloon car (sim/saloon.ts) */
  | 'saloon'
  /** a block (pier, lintel, capstone) of a stacked stone archway (sim/destruct.ts): a big blast topples it */
  | 'stone'
  /** a fallen building's rubble heap (sim/destruct.ts) */
  | 'rubble';

export interface Box {
  kind: ObstacleKind;
  x: number; z: number; hx: number; hz: number; y0: number; y1: number;
  /** cosmetic variation index for the client */
  variant: number;
}
export interface Cyl {
  kind: ObstacleKind;
  x: number; z: number; r: number; y0: number; y1: number;
  variant: number;
}

export interface RayHit {
  t: number;
  kind: 0 | 1 | 2 | 3; // none, terrain, box, cyl
  nx: number; ny: number; nz: number;
}

/** A raised table of rock: soft = a walkable mesa, hard = a cliff-walled butte you fly up to. */
export interface Plateau { x: number; z: number; r: number; h: number; soft: number; seed: number }
/** A trench cut through an island: walls you can't walk up, a floor `depth` below the rim. */
export interface Canyon { pts: { x: number; z: number }[]; hw: number; floor: number; seed: number }
/** Still water. `y` is the surface; the basin is carved `depth` below it. */
export interface Pond { x: number; z: number; r: number; y: number; depth: number }
/** One straight run of a stream, with its surface height at both ends (it only ever runs downhill). */
export interface StreamSeg {
  ax: number; az: number; bx: number; bz: number; ya: number; yb: number;
  /** half width of the water */
  hw: number;
  ux: number; uz: number; len: number;
  /** bounds including the banks, for a fast reject */
  x0: number; x1: number; z0: number; z1: number;
}
export interface Stream { segs: StreamSeg[]; hw: number; depth: number }
/** A falling sheet of water: off an island's edge, or down a cliff inside it. */
export interface Fall {
  x: number; z: number;
  /** the way the water leaves the lip (unit) */
  ux: number; uz: number;
  hw: number;
  /** lip (top) and where it lands (or the haze) */
  y1: number; y0: number;
  /** pours off the island into the sky (true) or down a cliff onto more island (false) */
  edge: boolean;
}

/** Islands per village: two combat islands, then the boss island (sim/run.ts). */
export const ISLANDS_PER_VILLAGE = 3;
export const enum IslandKind { Combat = 0, Boss = 1, Bonus = 2 }
/** Bonus islands sit outside the village numbering: index BONUS_INDEX + village. */
export const BONUS_INDEX = 1000;
export const islandKindOf = (index: number) => (index >= BONUS_INDEX ? IslandKind.Bonus
  : index % ISLANDS_PER_VILLAGE === ISLANDS_PER_VILLAGE - 1 ? IslandKind.Boss : IslandKind.Combat);
/** The second combat island of each village has the bonus door, up on a sky islet. */
export const hasBonusDoor = (index: number) => index < BONUS_INDEX && index % ISLANDS_PER_VILLAGE === 1;

/** Island archetypes (sim/landforms.ts). */
export const enum Arch { Falls = 0, Canyon = 1, Shattered = 2, Lagoon = 3, Bastion = 4 }
export const ARCH_COUNT = 5;
export const ARCH_NAMES = ['THE FALLS', 'THE CANYON', 'THE SHATTERS', 'THE LAGOON', 'THE BASTION'] as const;

/** The saloon-door gate (sim/run.ts, client/render/world/gate.ts): doorway size and post layout. */
export const GATE = { halfW: 1.78, height: 5.3, postHalf: 0.4, beamY: 5.3, modelScale: 6.8 } as const;

/** Water's grip on the heat economy (sim/player.ts reads these). */
const WET_CELL = 2;
export const WATER = {
  /** how high above the surface a flier still skims (full strength at the surface, none at this height) */
  skimH: 4.0,
} as const;

export class World {
  /** layout seed of THIS island: the run seed for island 0, hashed per island after that */
  readonly seed: number;
  /** the room's run seed (what the Welcome carries) */
  readonly runSeed: number;
  /** island number across the whole run: village * ISLANDS_PER_VILLAGE + island */
  readonly index: number;
  readonly kind: IslandKind;
  readonly arch: Arch;
  readonly islands: Island[] = [];
  readonly boxes: Box[] = [];
  readonly cyls: Cyl[] = [];
  readonly enemySpawns: { x: number; z: number }[] = [];
  readonly playerSpawn = { x: -52, z: -42 };
  readonly path: { x: number; z: number }[] = [];
  readonly town = { x0: 34, x1: 92, z: 0, halfWidth: 6, y: 0 };
  readonly arena = { x: 0, z: 0, r: 30 };
  /** the walkable mesa (r = 0 when the archetype has none) */
  readonly mesa = { x: -42, z: 38, r: 20, h: 7 };
  readonly gate = { x: 98, z: 0 };
  readonly plateaus: Plateau[] = [];
  readonly canyons: Canyon[] = [];
  readonly ponds: Pond[] = [];
  readonly streams: Stream[] = [];
  readonly falls: Fall[] = [];
  /** stacked stone archways (sim/destruct.ts) */
  readonly archways: Archway[] = [];
  /** what's been shot up, burnt down and knocked over on this island (sim/destruct.ts) */
  readonly dest: Destruct;
  /** bounds of all water + banks, for a fast reject in terrain() */
  private wx0 = 1e9; private wx1 = -1e9; private wz0 = 1e9; private wz1 = -1e9;

  /** `arch` forces an archetype (lab pages, tests); otherwise the seed picks one. */
  constructor(seed: number, index = 0, arch?: Arch) {
    this.runSeed = seed >>> 0;
    this.index = index | 0;
    this.kind = islandKindOf(this.index);
    // island 0 keeps the run seed, so the first island of every room looks exactly as before
    this.seed = this.index === 0 ? this.runSeed : hash32(this.runSeed, this.index, 0x15a1);
    this.arch = arch ?? pickArch(this.runSeed, this.index, this.kind);
    const rng = new Rng(this.seed ^ 0x1a4d);
    layoutLand(this, rng);
    this.town.y = this.rawHeight(this.islands[0], 60, 0, true);
    this.buildTown();
    // water is carved after the land it runs over exists, and before anything is placed on it
    layoutWater(this, rng);
    this.finishWater();
    layoutForms(this, rng);
    this.buildScatter();
    this.buildSpawns();
    this.buildPath();
    layoutArchways(this, this.archways);
    // the saloon-door gate: two solid posts and the sign beam over them (drawn by the gate
    // view, not as props); you walk, run or fly through the doorway between the posts
    const gy = this.gateY();
    for (const s of [-1, 1]) {
      this.boxes.push({ kind: 'gate', x: this.gate.x, z: this.gate.z + s * (GATE.halfW + GATE.postHalf), hx: GATE.postHalf, hz: GATE.postHalf, y0: gy - 0.6, y1: gy + GATE.beamY + 0.9, variant: 0 });
    }
    this.boxes.push({ kind: 'gate', x: this.gate.x, z: this.gate.z, hx: 0.3, hz: GATE.halfW + GATE.postHalf * 2 + 0.4, y0: gy + GATE.beamY, y1: gy + GATE.beamY + 0.55, variant: 1 });
    if (this.bonusDoor) {
      const b = this.bonusDoor, by = this.bonusDoorY();
      for (const s of [-1, 1]) {
        this.boxes.push({ kind: 'gate', x: b.x, z: b.z + s * (GATE.halfW + GATE.postHalf), hx: GATE.postHalf, hz: GATE.postHalf, y0: by - 0.6, y1: by + GATE.beamY + 0.9, variant: 2 });
      }
      this.boxes.push({ kind: 'gate', x: b.x, z: b.z, hx: 0.3, hz: GATE.halfW + GATE.postHalf * 2 + 0.4, y0: by + GATE.beamY, y1: by + GATE.beamY + 0.55, variant: 3 });
    }
    // the saloon car rides above every island (sim/saloon.ts); variant -1 keeps it out of the prop views
    addSaloonCollision(this.boxes, this.cyls);
    this.dest = new Destruct(this);
  }

  /** The bonus door: a second set of saloon doors up on a sky islet (sim/run.ts), or null. */
  get bonusDoor() { return hasBonusDoor(this.index) ? { x: this.islands[3].cx, z: this.islands[3].cz } : null; }
  bonusDoorY() { const b = this.islands[3]; const t = this.terrain(b.cx, b.cz); return t === NO_GROUND ? b.baseY : t; }

  /** Ground height at the gate (the doorway sill). */
  gateY() { return Math.max(this.terrain(this.gate.x, this.gate.z), 0); }

  /** The islet the extraction bell floats over and the train docks beside (sim/run.ts). */
  get extractIslet() { return this.islands[1]; }

  // ---------------------------------------------------------------- terrain
  /** Island edge radius in this direction (unit dx,dz), no trig. */
  edgeRadius(isl: Island, ux: number, uz: number) {
    const n = valueNoise(isl.seed, ux * 2.6 + 10, uz * 2.6 + 10);
    const n2 = valueNoise(isl.seed + 7, ux * 6.1 + 3, uz * 6.1 + 3);
    const er = isl.radius * (0.78 + 0.17 * n + 0.07 * n2);
    // the main island always reaches past the end of the street to the gate, whatever its size
    return isl.main ? Math.max(er, 114 * smoothstep(0.8, 0.96, ux)) : er;
  }

  /** The land before water: noise, then the archetype's landforms. */
  rawHeight(isl: Island, x: number, z: number, skipTown = false) {
    let h = isl.baseY + (fbm(isl.seed, x / 30, z / 30, 4) - 0.5) * 5.0;
    h += (fbm(isl.seed + 99, x / 9, z / 9, 2) - 0.5) * 0.7;
    if (isl.main) {
      // flatten the arena so the dance has a floor
      const da = len2(x - this.arena.x, z - this.arena.z);
      h = h + (isl.baseY + 0.2 - h) * smoothstep(34, 14, da) * 0.85;
      // the mesa: a raised plateau for sniping and diving off
      if (this.mesa.r > 0) {
        const dm = len2(x - this.mesa.x, z - this.mesa.z);
        h += this.mesa.h * smoothstep(this.mesa.r + 6, this.mesa.r - 2, dm);
      }
    } else {
      const d = len2(x - isl.cx, z - isl.cz) / isl.radius;
      h += 1.2 * smoothstep(0.9, 0.2, d);
    }
    // plateaus and buttes: an organic outline (noise on the bearing), a flat-ish table on top
    for (const p of this.plateaus) {
      const dx = x - p.x, dz = z - p.z;
      if (Math.abs(dx) > p.r * 1.15 + p.soft + 1 || Math.abs(dz) > p.r * 1.15 + p.soft + 1) continue;
      const d = len2(dx, dz);
      const ux = d > 1e-4 ? dx / d : 1, uz = d > 1e-4 ? dz / d : 0;
      const re = p.r * (0.86 + 0.28 * valueNoise(p.seed, ux * 2.2 + 5, uz * 2.2 + 5));
      const k = smoothstep(re + p.soft, re - 0.6, d);
      if (k > 0) h += p.h * k;
    }
    // canyons: a floor well below the rim, walls too steep to walk
    for (const c of this.canyons) {
      const d = polyDist(c.pts, x, z, c.hw * 1.3 + 2);
      if (d < 0) continue;
      const hw = c.hw * (0.82 + 0.36 * valueNoise(c.seed, x / 22, z / 22));
      const k = smoothstep(hw, hw * 0.55, d);
      if (k > 0) {
        const floor = c.floor + (valueNoise(c.seed + 3, x / 7, z / 7) - 0.5) * 0.6;
        h = h + (Math.min(h, floor) - h) * k;
      }
    }
    if (isl.main && !skipTown) {
      const t = this.townMask(x, z);
      if (t > 0) h = h + (this.town.y - h) * t;
    }
    return h;
  }

  townMask(x: number, z: number) {
    const t = this.town;
    const ax = smoothstep(t.x0 - 10, t.x0 - 2, x) * smoothstep(t.x1 + 10, t.x1 + 2, x);
    const az = smoothstep(26, 18, Math.abs(z - t.z));
    return ax * az;
  }

  islandAt(x: number, z: number): Island | null {
    for (const isl of this.islands) {
      const dx = x - isl.cx, dz = z - isl.cz;
      const d = len2(dx, dz);
      if (d > isl.radius * 1.05) continue;
      if (d < 1e-6) return isl;
      if (d <= this.edgeRadius(isl, dx / d, dz / d)) return isl;
    }
    return null;
  }

  /** Land height before water is carved (what streams are routed over), or NO_GROUND. */
  dryHeight(x: number, z: number): number {
    for (const isl of this.islands) {
      const dx = x - isl.cx, dz = z - isl.cz;
      const d = len2(dx, dz);
      if (d > isl.radius * 1.05) continue;
      const er = d < 1e-6 ? isl.radius : this.edgeRadius(isl, dx / d, dz / d);
      if (d > er) continue;
      let h = this.rawHeight(isl, x, z);
      // a soft lip before the cliff
      h -= 1.6 * smoothstep(er - 7, er, d);
      return h;
    }
    return NO_GROUND;
  }

  /** Terrain height, or NO_GROUND off the islands. */
  terrain(x: number, z: number): number {
    const h = this.dryHeight(x, z);
    if (h === NO_GROUND || x < this.wx0 || x > this.wx1 || z < this.wz0 || z > this.wz1) return h;
    return this.carve(x, z, h);
  }

  // ---------------------------------------------------------------- water
  /** Lower the land into pond basins and stream beds; banks ease back up to the land. */
  private carve(x: number, z: number, h: number) {
    for (const p of this.ponds) {
      const dx = x - p.x, dz = z - p.z, bank = 3.5;
      if (Math.abs(dx) > p.r + bank || Math.abs(dz) > p.r + bank) continue;
      const d = len2(dx, dz);
      if (d < p.r) { const f = d / p.r; h = Math.min(h, p.y - p.depth * (1 - f * f) - 0.25 * f); }
      else if (d < p.r + bank) h = Math.min(h, p.y + 0.12 + (h - p.y - 0.12) * smoothstep(p.r, p.r + bank, d));
    }
    for (const s of this.streams) {
      for (const g of s.segs) {
        if (x < g.x0 || x > g.x1 || z < g.z0 || z > g.z1) continue;
        const rx = x - g.ax, rz = z - g.az;
        const t = clamp(rx * g.ux + rz * g.uz, 0, g.len);
        const qx = rx - g.ux * t, qz = rz - g.uz * t;
        const d = Math.sqrt(qx * qx + qz * qz);
        const y = g.ya + (g.yb - g.ya) * (t / g.len);
        const bank = 3 + g.hw * 0.4;
        if (d < g.hw) { const f = d / g.hw; h = Math.min(h, y - s.depth * (1 - f * f) - 0.2 * f); }
        else if (d < g.hw + bank) h = Math.min(h, y + 0.1 + (h - y - 0.1) * smoothstep(g.hw, g.hw + bank, d));
      }
    }
    return h;
  }

  /** Water surface height at (x,z), or NO_GROUND if it's dry. */
  waterAt(x: number, z: number): number {
    if (x < this.wx0 || x > this.wx1 || z < this.wz0 || z > this.wz1) return NO_GROUND;
    let best = NO_GROUND;
    for (const p of this.ponds) {
      const dx = x - p.x, dz = z - p.z;
      if (dx * dx + dz * dz < p.r * p.r && p.y > best) best = p.y;
    }
    for (const s of this.streams) {
      for (const g of s.segs) {
        if (x < g.x0 || x > g.x1 || z < g.z0 || z > g.z1) continue;
        const rx = x - g.ax, rz = z - g.az;
        const t = clamp(rx * g.ux + rz * g.uz, 0, g.len);
        const qx = rx - g.ux * t, qz = rz - g.uz * t;
        if (qx * qx + qz * qz < g.hw * g.hw) {
          const y = g.ya + (g.yb - g.ya) * (t / g.len);
          if (y > best) best = y;
        }
      }
    }
    return best;
  }

  /**
   * Is (x,z) water a robot would wade in (deeper than ankle height)? A 2 m grid over the water's
   * bounding box, built on first use, so pathing can ask it many times a tick for free.
   */
  wetAt(x: number, z: number): boolean {
    if (x < this.wx0 || x > this.wx1 || z < this.wz0 || z > this.wz1) return false;
    if (!this.wetGrid) {
      const nx = Math.ceil((this.wx1 - this.wx0) / WET_CELL) + 1, nz = Math.ceil((this.wz1 - this.wz0) / WET_CELL) + 1;
      const grid = new Uint8Array(nx * nz);
      for (let j = 0; j < nz; j++) for (let i = 0; i < nx; i++) {
        const cx = this.wx0 + i * WET_CELL, cz = this.wz0 + j * WET_CELL;
        const wy = this.waterAt(cx, cz);
        if (wy !== NO_GROUND && wy - this.terrain(cx, cz) > 0.3) grid[j * nx + i] = 1;
      }
      this.wetGrid = grid; this.wetNx = nx;
    }
    const i = Math.round((x - this.wx0) / WET_CELL), j = Math.round((z - this.wz0) / WET_CELL);
    return this.wetGrid[j * this.wetNx + i] === 1;
  }
  private wetGrid: Uint8Array | null = null;
  private wetNx = 0;

  /** 0..1: how deep inside a falling sheet of water (x,y,z) is. */
  inFall(x: number, y: number, z: number): number {
    for (const f of this.falls) {
      if (y > f.y1 + 1.5 || y < f.y0 - 1) continue;
      const rx = x - f.x, rz = z - f.z;
      const along = rx * f.ux + rz * f.uz;
      if (along < -1.8 || along > 4.5) continue;
      const lat = Math.abs(rx * f.uz - rz * f.ux);
      if (lat < f.hw + 1.2) return 1 - Math.max(0, lat - f.hw) / 1.2;
    }
    return 0;
  }

  /**
   * Route a stream over the dry land: its surface only ever runs downhill (it cuts a gorge
   * where the land rises), drops off cliffs as cascades, and pours off the island at the edge.
   * `pts` are control points; `spring` is a pond it starts from (its surface caps the stream).
   */
  addStream(pts: { x: number; z: number }[], hw: number, depth: number, startY?: number, pools: { x: number; z: number; r: number }[] = []) {
    // densify to ~2 m
    const dense: { x: number; z: number }[] = [];
    for (let i = 0; i < pts.length - 1; i++) {
      const a = pts[i], b = pts[i + 1];
      const n = Math.max(1, Math.ceil(len2(b.x - a.x, b.z - a.z) / 2));
      for (let j = 0; j < n; j++) dense.push({ x: a.x + (b.x - a.x) * (j / n), z: a.z + (b.z - a.z) * (j / n) });
    }
    dense.push(pts[pts.length - 1]);
    // surface: the lowest bank on either side, minus a freeboard, never rising
    const ys: number[] = [];
    let y = startY ?? 1e9, lip = -1, lipX = 0, lipZ = 0, rimRun = 0;
    // pools along the way: the nearest dense sample to each becomes a still, wide stretch
    const poolAt = pools.map((pl) => {
      let bi = 0, bd = 1e18;
      for (let i = 0; i < dense.length; i++) { const d = (dense[i].x - pl.x) ** 2 + (dense[i].z - pl.z) ** 2; if (d < bd) { bd = d; bi = i; } }
      return bi;
    });
    for (let i = 0; i < dense.length; i++) {
      const p = dense[i];
      const q = dense[Math.min(i + 1, dense.length - 1)], o = dense[Math.max(i - 1, 0)];
      let dx = q.x - o.x, dz = q.z - o.z; const l = len2(dx, dz) || 1; dx /= l; dz /= l;
      const hc = this.dryHeight(p.x, p.z);
      if (hc === NO_GROUND) { lip = i; break; }
      const hl = this.dryHeight(p.x - dz * (hw + 2), p.z + dx * (hw + 2));
      const hr = this.dryHeight(p.x + dz * (hw + 2), p.z - dx * (hw + 2));
      // a stream running along the rim with one bank in the air would be a raised strip: it
      // pours off the side there instead, turned toward the drop
      rimRun = hl === NO_GROUND || hr === NO_GROUND ? rimRun + 1 : 0;
      if (i > 4 && rimRun >= 3) {
        const side = (hl === NO_GROUND ? 1 : 0) - (hr === NO_GROUND ? 1 : 0); // both gone: straight on
        lip = i; lipX = dx - dz * side * 1.5; lipZ = dz + dx * side * 1.5; break;
      }
      let bankY = hc;
      if (hl !== NO_GROUND) bankY = Math.min(bankY, hl);
      if (hr !== NO_GROUND) bankY = Math.min(bankY, hr);
      y = Math.min(y - 0.015, bankY - 0.45);
      const k = poolAt.indexOf(i);
      if (k >= 0) {
        const pl = pools[k];
        let rim = y + 0.35;
        for (let j = 0; j < 16; j++) {
          const a = (j / 16) * Math.PI * 2;
          const h = this.dryHeight(pl.x + Math.cos(a) * (pl.r + 2), pl.z + Math.sin(a) * (pl.r + 2));
          if (h !== NO_GROUND) rim = Math.min(rim, h);
        }
        y = Math.min(y, rim - 0.35);
        this.ponds.push({ x: pl.x, z: pl.z, r: pl.r, y, depth: depth + 0.6 });
      }
      ys.push(y);
    }
    const n = lip < 0 ? dense.length : lip;
    if (n < 2) return;
    const segs: StreamSeg[] = [];
    // cascades: a drop of more than ~1.5 m between samples is a fall down a cliff
    let fallTop = -1;
    for (let i = 0; i < n - 1; i++) {
      const a = dense[i], b = dense[i + 1];
      const drop = ys[i] - ys[i + 1];
      if (drop > 1.0) {
        if (fallTop < 0) fallTop = i;
      } else if (fallTop >= 0) {
        const t = dense[fallTop], e = dense[i];
        let ux = e.x - t.x, uz = e.z - t.z; const l = len2(ux, uz) || 1; ux /= l; uz /= l;
        if (ys[fallTop] - ys[i] > 2.2) this.falls.push({ x: t.x, z: t.z, ux, uz, hw, y1: ys[fallTop], y0: ys[i], edge: false });
        fallTop = -1;
      }
      const len = len2(b.x - a.x, b.z - a.z);
      if (len < 1e-3) continue;
      segs.push(seg(a.x, a.z, b.x, b.z, ys[i], ys[i + 1], hw));
    }
    // the edge: pour off the island; land on whatever plate is underneath, or fall into the haze
    if (lip > 0) {
      const a = dense[lip - 1], b = dense[Math.min(lip, dense.length - 1)];
      let ux = lipX || b.x - a.x, uz = lipZ || b.z - a.z; const l = len2(ux, uz) || 1; ux /= l; uz /= l;
      // run the water right out to the rim so the sheet hangs off the cliff, not short of it
      const ex = a.x + ux * 2.2, ez = a.z + uz * 2.2;
      const yl = ys[lip - 1];
      segs.push(seg(a.x, a.z, ex, ez, yl, yl - 0.05, hw));
      let y0 = yl - 70;
      for (let k = 4; k < 40; k += 3) {
        const g = this.dryHeight(ex + ux * k, ez + uz * k);
        if (g !== NO_GROUND && g < yl - 3) { y0 = g; break; }
      }
      this.falls.push({ x: ex, z: ez, ux, uz, hw: hw * 0.92, y1: yl - 0.05, y0, edge: true });
    }
    this.streams.push({ segs, hw, depth });
  }

  /** A pond whose surface sits just under the lowest point of its rim. */
  addPond(x: number, z: number, r: number, depth: number) {
    let rim = this.dryHeight(x, z);
    if (rim === NO_GROUND) return null;
    for (let i = 0; i < 16; i++) {
      const a = (i / 16) * Math.PI * 2;
      const h = this.dryHeight(x + Math.cos(a) * (r + 2), z + Math.sin(a) * (r + 2));
      if (h !== NO_GROUND) rim = Math.min(rim, h);
    }
    const p: Pond = { x, z, r, y: rim - 0.35, depth };
    this.ponds.push(p);
    return p;
  }

  private finishWater() {
    const grow = (x0: number, z0: number, x1: number, z1: number) => {
      this.wx0 = Math.min(this.wx0, x0); this.wx1 = Math.max(this.wx1, x1);
      this.wz0 = Math.min(this.wz0, z0); this.wz1 = Math.max(this.wz1, z1);
    };
    for (const p of this.ponds) grow(p.x - p.r - 4, p.z - p.r - 4, p.x + p.r + 4, p.z + p.r + 4);
    for (const s of this.streams) for (const g of s.segs) grow(g.x0, g.z0, g.x1, g.z1);
  }

  /** Is (x,z) clear of water and its banks by `m` metres? (for placing things) */
  dryAt(x: number, z: number, m = 0) {
    for (const p of this.ponds) if (len2(x - p.x, z - p.z) < p.r + 3.5 + m) return false;
    for (const s of this.streams) for (const g of s.segs) {
      const rx = x - g.ax, rz = z - g.az;
      const t = clamp(rx * g.ux + rz * g.uz, 0, g.len);
      if (len2(rx - g.ux * t, rz - g.uz * t) < g.hw + 3 + g.hw * 0.4 + m) return false;
    }
    return true;
  }

  // ---------------------------------------------------------------- collision
  /** Highest walkable surface under (x,z) at or below y + step. */
  support(x: number, z: number, y: number, step = 0.55): number {
    let best = this.terrain(x, z);
    for (const b of this.boxes) {
      if (b.y1 > y + step || b.y1 <= best) continue;
      if (Math.abs(x - b.x) <= b.hx + 0.15 && Math.abs(z - b.z) <= b.hz + 0.15) best = b.y1;
    }
    for (const c of this.cyls) {
      if (c.y1 > y + step || c.y1 <= best) continue;
      const dx = x - c.x, dz = z - c.z;
      if (dx * dx + dz * dz <= (c.r + 0.1) * (c.r + 0.1)) best = c.y1;
    }
    return best;
  }

  /**
   * Cliffs: terrain that rises faster than a steep slope between two points is a wall, not
   * ground to be stepped onto. Returns true if moving from (ax,az) at foot height y to (bx,bz)
   * would climb one.
   */
  cliff(ax: number, az: number, y: number, bx: number, bz: number) {
    const g = this.terrain(bx, bz);
    if (g === NO_GROUND) return false;
    return g > y + 0.14 + 1.25 * len2(bx - ax, bz - az);
  }

  /** Push a vertical capsule (radius r, from y to y+h) out of obstacles. Mutates p. */
  pushOut(p: { x: number; y: number; z: number; vy?: number }, r: number, h: number, skip: Box['kind'] | null = null) {
    saloonCeiling(p, h);
    for (const b of this.boxes) {
      if (p.y >= b.y1 - 0.05 || p.y + h <= b.y0 || b.kind === skip) continue;
      const cx = clamp(p.x, b.x - b.hx, b.x + b.hx);
      const cz = clamp(p.z, b.z - b.hz, b.z + b.hz);
      let dx = p.x - cx, dz = p.z - cz;
      const d2 = dx * dx + dz * dz;
      if (d2 >= r * r) continue;
      if (d2 > 1e-8) {
        const d = Math.sqrt(d2);
        p.x = cx + (dx / d) * r;
        p.z = cz + (dz / d) * r;
      } else {
        // centre inside the box: exit along the shallowest axis
        const px = b.hx - Math.abs(p.x - b.x), pz = b.hz - Math.abs(p.z - b.z);
        if (px < pz) p.x = b.x + Math.sign(p.x - b.x || 1) * (b.hx + r);
        else p.z = b.z + Math.sign(p.z - b.z || 1) * (b.hz + r);
      }
    }
    for (const c of this.cyls) {
      if (p.y >= c.y1 - 0.05 || p.y + h <= c.y0) continue;
      const dx = p.x - c.x, dz = p.z - c.z;
      const rr = c.r + r;
      const d2 = dx * dx + dz * dz;
      if (d2 >= rr * rr) continue;
      const d = Math.sqrt(d2) || 1e-4;
      p.x = c.x + (dx / d) * rr;
      p.z = c.z + (dz / d) * rr;
    }
  }

  /**
   * A flier's head meeting the underside of a shelf or an arch: stop the climb. (Walls are
   * pushOut's job; this is only the ceiling.) Returns the clamped y.
   */
  ceiling(x: number, z: number, y: number, h: number, prevY: number) {
    let top = 1e9;
    for (const b of this.boxes) {
      if (b.y0 < prevY + h - 0.05 || b.y0 > top) continue;
      if (Math.abs(x - b.x) <= b.hx && Math.abs(z - b.z) <= b.hz) top = b.y0;
    }
    for (const c of this.cyls) {
      if (c.y0 < prevY + h - 0.05 || c.y0 > top) continue;
      const dx = x - c.x, dz = z - c.z;
      if (dx * dx + dz * dz <= c.r * c.r) top = c.y0;
    }
    return y + h > top ? top - h : y;
  }

  /** Ray against terrain + obstacles. Direction must be unit length. */
  raycast(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, maxT: number, out: RayHit): RayHit {
    out.t = maxT; out.kind = 0; out.nx = 0; out.ny = 1; out.nz = 0;
    // boxes (slab test)
    for (const b of this.boxes) {
      const t = rayBox(ox, oy, oz, dx, dy, dz, b.x - b.hx, b.y0, b.z - b.hz, b.x + b.hx, b.y1, b.z + b.hz, out.t);
      if (t >= 0 && t < out.t) {
        out.t = t; out.kind = 2;
        const hx = ox + dx * t - b.x, hy = oy + dy * t - (b.y0 + b.y1) / 2, hz = oz + dz * t - b.z;
        const ax = Math.abs(hx) / b.hx, ay = Math.abs(hy) / ((b.y1 - b.y0) / 2), az = Math.abs(hz) / b.hz;
        out.nx = out.ny = out.nz = 0;
        if (ax >= ay && ax >= az) out.nx = Math.sign(hx);
        else if (ay >= az) out.ny = Math.sign(hy);
        else out.nz = Math.sign(hz);
      }
    }
    for (const c of this.cyls) {
      const t = rayCyl(ox, oy, oz, dx, dy, dz, c, out.t);
      if (t >= 0 && t < out.t) {
        out.t = t; out.kind = 3;
        const hx = ox + dx * t - c.x, hz = oz + dz * t - c.z;
        const hy = oy + dy * t;
        if (hy > c.y1 - 0.02) { out.nx = 0; out.ny = 1; out.nz = 0; }
        else if (hy < c.y0 + 0.02) { out.nx = 0; out.ny = -1; out.nz = 0; }
        else { const l = len2(hx, hz) || 1; out.nx = hx / l; out.ny = 0; out.nz = hz / l; }
      }
    }
    // terrain march
    const step = 0.7;
    let prevT = 0;
    for (let t = step; t < out.t + step; t += step) {
      const tt = Math.min(t, out.t);
      const x = ox + dx * tt, y = oy + dy * tt, z = oz + dz * tt;
      const h = this.terrain(x, z);
      const above = h === NO_GROUND ? 1e9 : y - h;
      if (above < 0) {
        let lo = prevT, hi = tt;
        for (let i = 0; i < 6; i++) {
          const m = (lo + hi) / 2;
          const hm = this.terrain(ox + dx * m, oz + dz * m);
          if (hm !== NO_GROUND && oy + dy * m - hm < 0) hi = m; else lo = m;
        }
        out.t = hi; out.kind = 1;
        const hx = ox + dx * hi, hz = oz + dz * hi;
        const e = 0.4;
        const hl = this.terrain(hx - e, hz), hr = this.terrain(hx + e, hz);
        const hb = this.terrain(hx, hz - e), hf = this.terrain(hx, hz + e);
        if (hl !== NO_GROUND && hr !== NO_GROUND && hb !== NO_GROUND && hf !== NO_GROUND) {
          const nx = hl - hr, nz = hb - hf, ny = 2 * e;
          const l = Math.sqrt(nx * nx + ny * ny + nz * nz);
          out.nx = nx / l; out.ny = ny / l; out.nz = nz / l;
        }
        return out;
      }
      prevT = tt;
      if (tt >= out.t) break;
    }
    return out;
  }

  /** True if the segment a→b is unobstructed. */
  lineOfSight(ax: number, ay: number, az: number, bx: number, by: number, bz: number) {
    const dx = bx - ax, dy = by - ay, dz = bz - az;
    const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (d < 1e-3) return true;
    const hit = this.raycast(ax, ay, az, dx / d, dy / d, dz / d, d - 0.3, scratchHit);
    return hit.kind === 0;
  }

  // ---------------------------------------------------------------- layout
  private buildTown() {
    const rng = new Rng(this.seed ^ 0x70f1);
    const t = this.town;
    for (const side of [-1, 1]) {
      let x = t.x0 + 2;
      let v = side > 0 ? 0 : 3;
      while (x < t.x1 - 4) {
        const w = rng.range(7, 10.5);
        const d = rng.range(7.5, 10);
        const h = rng.pick([5.5, 6.5, 8.5, 9.5]);
        const cz = t.z + side * (t.halfWidth + 2.2 + d / 2);
        const y0 = t.y - 1.5;
        this.boxes.push({ kind: 'building', x: x + w / 2, z: cz, hx: w / 2, hz: d / 2, y0, y1: t.y + h, variant: v++ % 6 });
        x += w + rng.range(1.2, 3.5);
      }
    }
    // water tower + crates on the boardwalks (cover that keeps the street clear)
    this.cyls.push({ kind: 'tank', x: t.x1 + 6, z: 15, r: 3.2, y0: t.y - 1, y1: t.y + 11, variant: 0 });
    for (let i = 0; i < 7; i++) {
      const x = t.x0 + 6 + i * 8 + rng.range(-1, 1);
      const z = t.z + (i % 2 ? 1 : -1) * (t.halfWidth + 1.5);
      this.boxes.push({ kind: 'crate', x, z, hx: 0.6, hz: 0.6, y0: t.y - 0.2, y1: t.y + 1.2, variant: i });
      this.cyls.push({ kind: 'pole', x: x + 3, z: t.z + (i % 2 ? -1 : 1) * (t.halfWidth + 0.8), r: 0.18, y0: t.y - 0.5, y1: t.y + 8.5, variant: i });
    }
  }

  /** Is (x,z) clear of every obstacle by `r` + margin? */
  clearOf(x: number, z: number, r: number, margin = 1.5) {
    for (const c of this.cyls) if (len2(x - c.x, z - c.z) < r + c.r + margin) return false;
    for (const b of this.boxes) if (Math.abs(x - b.x) < b.hx + r + margin && Math.abs(z - b.z) < b.hz + r + margin) return false;
    return true;
  }

  /** Flat-ish dry ground (no cliff, canyon floor or water within a few metres) at (x,z)? */
  openGround(x: number, z: number) {
    const h = this.terrain(x, z);
    if (h === NO_GROUND) return false;
    for (const [ox, oz] of [[3, 0], [-3, 0], [0, 3], [0, -3]]) {
      const g = this.terrain(x + ox, z + oz);
      if (g === NO_GROUND || Math.abs(g - h) > 1.6) return false;
    }
    return this.dryAt(x, z, 1);
  }

  private buildScatter() {
    const rng = new Rng(this.seed ^ 0x5ca7);
    const main = this.islands[0];
    const place = (minR: number, check: (x: number, z: number) => boolean) => {
      for (let tries = 0; tries < 40; tries++) {
        const a = rng.range(0, Math.PI * 2);
        const r = rng.range(minR, main.radius * 0.82);
        const x = main.cx + Math.cos(a) * r, z = main.cz + Math.sin(a) * r;
        if (this.townMask(x, z) > 0.01) continue;
        if (len2(x - this.playerSpawn.x, z - this.playerSpawn.z) < 9) continue;
        if (!check(x, z)) continue;
        if (!this.openGround(x, z)) continue;
        return { x, z };
      }
      return null;
    };
    const clear = (x: number, z: number, r: number) => this.clearOf(x, z, r);
    // tall rock pillars ring the arena: cover and perches
    for (let i = 0; i < 7; i++) {
      const a = (i / 7) * Math.PI * 2 + rng.range(-0.2, 0.2);
      const r = rng.range(20, 27);
      const x = Math.cos(a) * r, z = Math.sin(a) * r;
      if (this.townMask(x, z) > 0.01 || !this.dryAt(x, z, 1) || !clear(x, z, 2.6)) continue;
      const rad = rng.range(1.6, 2.6);
      const y0 = this.terrain(x, z) - 1;
      this.cyls.push({ kind: 'pillar', x, z, r: rad, y0, y1: y0 + rng.range(4, 9), variant: i });
    }
    // rocks
    for (let i = 0; i < 46; i++) {
      const r = rng.range(0.7, 2.3);
      const p = place(14, (x, z) => clear(x, z, r));
      if (!p) continue;
      const y0 = this.terrain(p.x, p.z) - 0.6;
      this.cyls.push({ kind: 'rock', x: p.x, z: p.z, r, y0, y1: y0 + r * rng.range(0.7, 1.4) + 0.4, variant: i });
    }
    // a few big boulders out in the meadow: hard cover, and dark masses that frame the view
    for (let i = 0; i < 3; i++) {
      const r = rng.range(3.4, 4.4);
      const p = place(30, (x, z) => clear(x, z, r + 2));
      if (!p) continue;
      const y0 = this.terrain(p.x, p.z) - 0.8;
      this.cyls.push({ kind: 'rock', x: p.x, z: p.z, r, y0, y1: y0 + r * rng.range(0.9, 1.2) + 0.4, variant: 46 + i });
    }
    // ruins: low broken walls of somebody's old house
    for (let i = 0; i < 16; i++) {
      const p = place(16, (x, z) => clear(x, z, 4));
      if (!p) continue;
      const along = rng.chance(0.5);
      const len = rng.range(2.5, 6);
      const y0 = this.terrain(p.x, p.z) - 0.8;
      this.boxes.push({
        kind: 'ruin', x: p.x, z: p.z, hx: along ? len : 0.45, hz: along ? 0.45 : len,
        y0, y1: y0 + rng.range(1.6, 3.4), variant: i,
      });
    }
    // crates near the arena edge
    for (let i = 0; i < 10; i++) {
      const p = place(12, (x, z) => clear(x, z, 1.5) && len2(x, z) < 40);
      if (!p) continue;
      const y0 = this.terrain(p.x, p.z) - 0.1;
      this.boxes.push({ kind: 'crate', x: p.x, z: p.z, hx: 0.75, hz: 0.75, y0, y1: y0 + 1.5, variant: i });
    }
    // rocks on the islets
    for (let k = 1; k < this.islands.length; k++) {
      const isl = this.islands[k];
      const n = Math.round(clamp(isl.radius / 4, 5, 12));
      for (let i = 0; i < n; i++) {
        const a = rng.range(0, Math.PI * 2), rr = rng.range(4, isl.radius * 0.6);
        const x = isl.cx + Math.cos(a) * rr, z = isl.cz + Math.sin(a) * rr;
        if (this.terrain(x, z) === NO_GROUND || !this.dryAt(x, z) || !clear(x, z, 2)) continue;
        const r = rng.range(0.8, 1.8);
        const y0 = this.terrain(x, z) - 0.5;
        this.cyls.push({ kind: 'rock', x, z, r, y0, y1: y0 + r + 0.6, variant: 100 + i });
      }
    }
  }

  private buildSpawns() {
    for (let i = 0; i < 16; i++) {
      const a = (i / 16) * Math.PI * 2 + 0.13;
      const r = 44 + (i % 3) * 7;
      const x = Math.cos(a) * r, z = Math.sin(a) * r;
      if (!this.openGround(x, z) || !this.clearOf(x, z, 1, 0.5)) continue;
      if (len2(x - this.playerSpawn.x, z - this.playerSpawn.z) < 25) continue;
      this.enemySpawns.push({ x, z });
    }
    const t = this.town;
    for (let x = t.x0 + 10; x < t.x1; x += 16) this.enemySpawns.push({ x, z: t.z });
  }

  private buildPath() {
    const pts = [
      { x: this.playerSpawn.x, z: this.playerSpawn.z },
      { x: -30, z: -24 },
      { x: -8, z: -6 },
      { x: 12, z: 2 },
      { x: this.town.x0 - 4, z: 0 },
      { x: this.town.x1 + 2, z: 0 },
      { x: this.gate.x, z: this.gate.z },
    ];
    // densify with a gentle wobble
    for (let i = 0; i < pts.length - 1; i++) {
      const a = pts[i], b = pts[i + 1];
      const n = Math.max(2, Math.ceil(len2(b.x - a.x, b.z - a.z) / 3));
      for (let j = 0; j < n; j++) {
        const t = j / n;
        const w = (valueNoise(this.seed ^ 0xbeef, (i * 10 + t * 4), 0.5) - 0.5) * (a.x > this.town.x0 - 6 ? 0 : 3.5);
        const dx = b.x - a.x, dz = b.z - a.z, l = len2(dx, dz) || 1;
        this.path.push({ x: a.x + dx * t - (dz / l) * w, z: a.z + dz * t + (dx / l) * w });
      }
    }
    this.path.push(pts[pts.length - 1]);
  }

  spawnPoint(slot: number) {
    const a = slot * 1.7;
    const x = this.playerSpawn.x + Math.cos(a) * 2.5;
    const z = this.playerSpawn.z + Math.sin(a) * 2.5;
    return { x, y: this.support(x, z, 50) + 0.05, z };
  }
}

/** Which archetype an island gets: the two combat islands of a village always differ. */
export function pickArch(runSeed: number, index: number, kind: IslandKind): Arch {
  if (kind === IslandKind.Boss) return Arch.Bastion;
  // the bonus challenge island: the Shatters or the Canyon, the two that ask the most of a pack
  if (kind === IslandKind.Bonus) return hash32(runSeed, index, 0xb0) & 1 ? Arch.Shattered : Arch.Canyon;
  const village = Math.floor(index / ISLANDS_PER_VILLAGE), slot = index % ISLANDS_PER_VILLAGE;
  const h = hash32(runSeed, village, 0xa7c4);
  const first = h % 4;
  return (slot === 0 ? first : (first + 1 + ((h >>> 8) % 3)) % 4) as Arch;
}

function seg(ax: number, az: number, bx: number, bz: number, ya: number, yb: number, hw: number): StreamSeg {
  const len = len2(bx - ax, bz - az) || 1e-3;
  const m = hw + 3 + hw * 0.4 + 0.5;
  return {
    ax, az, bx, bz, ya, yb, hw, ux: (bx - ax) / len, uz: (bz - az) / len, len,
    x0: Math.min(ax, bx) - m, x1: Math.max(ax, bx) + m, z0: Math.min(az, bz) - m, z1: Math.max(az, bz) + m,
  };
}

/** Distance from (x,z) to a polyline, or -1 if it's further than `reject`. */
export function polyDist(pts: { x: number; z: number }[], x: number, z: number, reject: number) {
  let best = 1e18;
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i], b = pts[i + 1];
    if (x < Math.min(a.x, b.x) - reject || x > Math.max(a.x, b.x) + reject || z < Math.min(a.z, b.z) - reject || z > Math.max(a.z, b.z) + reject) continue;
    const abx = b.x - a.x, abz = b.z - a.z, l2 = abx * abx + abz * abz || 1e-6;
    const t = clamp(((x - a.x) * abx + (z - a.z) * abz) / l2, 0, 1);
    const qx = a.x + abx * t - x, qz = a.z + abz * t - z;
    const d2 = qx * qx + qz * qz;
    if (d2 < best) best = d2;
  }
  return best === 1e18 ? -1 : Math.sqrt(best);
}

const scratchHit: RayHit = { t: 0, kind: 0, nx: 0, ny: 0, nz: 0 };

function rayBox(
  ox: number, oy: number, oz: number, dx: number, dy: number, dz: number,
  x0: number, y0: number, z0: number, x1: number, y1: number, z1: number, maxT: number,
) {
  let tmin = 0, tmax = maxT;
  const ix = 1 / dx, iy = 1 / dy, iz = 1 / dz;
  let a = (x0 - ox) * ix, b = (x1 - ox) * ix;
  tmin = Math.max(tmin, Math.min(a, b)); tmax = Math.min(tmax, Math.max(a, b));
  a = (y0 - oy) * iy; b = (y1 - oy) * iy;
  tmin = Math.max(tmin, Math.min(a, b)); tmax = Math.min(tmax, Math.max(a, b));
  a = (z0 - oz) * iz; b = (z1 - oz) * iz;
  tmin = Math.max(tmin, Math.min(a, b)); tmax = Math.min(tmax, Math.max(a, b));
  if (tmax < tmin || Number.isNaN(tmin)) return -1;
  return tmin;
}

function rayCyl(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, c: Cyl, maxT: number) {
  let best = -1;
  const fx = ox - c.x, fz = oz - c.z;
  const a = dx * dx + dz * dz;
  if (a > 1e-9) {
    const b = 2 * (fx * dx + fz * dz);
    const cc = fx * fx + fz * fz - c.r * c.r;
    const disc = b * b - 4 * a * cc;
    if (disc >= 0) {
      const s = Math.sqrt(disc);
      const t0 = (-b - s) / (2 * a);
      if (t0 >= 0 && t0 < maxT) {
        const y = oy + dy * t0;
        if (y >= c.y0 && y <= c.y1) best = t0;
      }
    }
  }
  if (Math.abs(dy) > 1e-6) {
    // caps: the crown (from above) and the underside (a floating shelf, from below)
    for (let k = 0; k < 2; k++) {
      const tc = ((k === 0 ? c.y1 : c.y0) - oy) / dy;
      if (tc >= 0 && tc < maxT && (best < 0 || tc < best)) {
        const x = ox + dx * tc - c.x, z = oz + dz * tc - c.z;
        if (x * x + z * z <= c.r * c.r) best = tc;
      }
    }
  }
  return best;
}
